// src/components/admin/UserManagement.js - Complete admin user management interface
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useRoles } from '../../hooks/useRoles';
import { useApiCall } from '../../hooks/useApiCall';
import { useFeature } from '../../hooks/useFeature';
import apiService from '../../services/apiService';
import { exportToCSV, formatDateForCSV, formatArrayForCSV } from '../../utils/csvExport';
import BulkUserImportModal from './BulkUserImport';
import InviteLinksModal from './InviteLinksModal';
import './Admin.css';

const UserManagement = () => {
  const { user } = useAuth();
  const { userProfile } = useRoles();
  const { enabled: tenantContentEnabled } = useFeature('tenant_content');
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [issuedLinks, setIssuedLinks] = useState(null); // { title, intro, links } after invite/regenerate
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState(''); // What user types
  const [activeSearchQuery, setActiveSearchQuery] = useState(''); // What's actually searched
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
    hasMore: false
  });

  const { loading, error, makeRequest } = useApiCall();

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [activeSearchQuery, statusFilter]);

  useEffect(() => {
    if (userProfile && userProfile.tenant) {
      loadUsers();
      loadAvailableRoles();
    }
  }, [userProfile, statusFilter, activeSearchQuery, pagination.page]);

  const loadUsers = async (page = pagination.page) => {
    if (!userProfile?.tenant?.id) return;

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        status: statusFilter,
        ...(activeSearchQuery && { search: activeSearchQuery })
      });

      const data = await makeRequest(
        () => apiService.get(`/api/v1/tenants/${userProfile.tenant.id}/users?${params}`, user),
        'Loading users...'
      );

    if (data.success) {
      // console.log('Backend response data:', data);
      // console.log('Users array from backend:', data.data.users);
      // console.log('First user roles (if exists):', data.data.users?.[0]?.roles);
      setUsers(data.data.users || []);
      setPagination(data.data.pagination || pagination);
    }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadAvailableRoles = async () => {
    /*
    try {
      const data = await makeRequest(
        () => apiService.get('/api/v1/roles', user),
        'Loading roles...'
      );

      if (data.success) {
        setAvailableRoles(data.data || []);
      }
    } catch (err) {
      console.error('Error loading roles:', err);
    }
      */
    // Temporary hardcoded roles until backend endpoint is ready

      setAvailableRoles([
        { id: 'User', name: 'User', description: 'Standard user with limited access.' },
        { id: 'ContentManager', name: 'Content Manager', description: 'Can manage organization-specific content.' },
        { id: 'Coach', name: 'Coach', description: 'Reporting access limited to the cohorts they are assigned to coach.' },
        { id: 'Admin', name: 'Admin', description: 'Full access to all features and settings.' }
        ]);
    // setAvailableRoles(['User', 'ContentManager', 'Admin']);
  };

  const inviteUser = async (inviteData) => {
    if (!userProfile?.tenant?.id) return;

    try {
      const data = await makeRequest(
        () => apiService.post(`/api/v1/tenants/${userProfile.tenant.id}/users/invite`, inviteData, user),
        'Sending invitation...'
      );

      if (data.success) {
        setShowInviteModal(false);
        setIssuedLinks({
          title: 'Invitation created',
          intro: data.data.emailSent
            ? `An invitation email was sent to ${data.data.email}. If it doesn't arrive, share this link another way.`
            : `The invitation email to ${data.data.email} could not be sent. Share this link another way.`,
          links: [{ email: data.data.email, inviteLink: data.data.inviteLink }]
        });
        await loadUsers(); // Refresh user list
        return data;
      }
    } catch (err) {
      console.error('Error inviting user:', err);
      throw err;
    }
  };

  const regenerateInviteLink = async (invitedUser) => {
    if (!userProfile?.tenant?.id) return;
    if (!window.confirm(`Create a new invite link for ${invitedUser.email}? Their current link will stop working.`)) return;

    try {
      const data = await makeRequest(
        () => apiService.post(`/api/v1/tenants/${userProfile.tenant.id}/users/${invitedUser.id}/invite-link`, {}, user),
        'Creating invite link...'
      );

      if (data.success) {
        setIssuedLinks({
          title: 'New invite link',
          intro: 'No email was sent. Share this link with the user.',
          links: [{ email: data.data.email, inviteLink: data.data.inviteLink }]
        });
      }
    } catch (err) {
      console.error('Error regenerating invite link:', err);
      alert(err.message || 'Failed to create a new invite link.');
    }
  };

  const regenerateAllPendingLinks = async () => {
    if (!userProfile?.tenant?.id) return;
    if (!window.confirm('Create new invite links for every user who has not activated yet? Their current links will stop working.')) return;

    try {
      const data = await makeRequest(
        () => apiService.post(`/api/v1/tenants/${userProfile.tenant.id}/users/invite-links/regenerate-pending`, {}, user),
        'Creating invite links...'
      );

      if (data.success) {
        setIssuedLinks({
          title: `New invite links (${data.data.links.length})`,
          intro: 'No emails were sent. Share these links with the users.',
          links: data.data.links
        });
      }
    } catch (err) {
      console.error('Error regenerating invite links:', err);
      alert(err.message || 'Failed to create new invite links.');
    }
  };

  const updateUser = async (userId, updates) => {
    if (!userProfile?.tenant?.id) return;

    try {
      const data = await makeRequest(
        () => apiService.put(`/api/v1/tenants/${userProfile.tenant.id}/users/${userId}`, updates, user),
        'Updating user...'
      );

      if (data.success) {
        await loadUsers(); // Refresh user list
        return data;
      }
    } catch (err) {
      console.error('Error updating user:', err);
      throw err;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'status-pill active';
      case 'invited': return 'status-pill invited';
      case 'suspended': return 'status-pill suspended';
      default: return 'status-pill archived';
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearchQuery(searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
  };

  const handleDownloadCSV = () => {
    if (!users || users.length === 0) {
      alert('No users available to export');
      return;
    }

    const columns = [
      { key: 'id', label: 'User ID' },
      { key: 'email', label: 'Email' },
      { key: 'displayName', label: 'Display Name' },
      { key: 'status', label: 'Status' },
      { 
        key: 'roles', 
        label: 'Roles',
        format: formatArrayForCSV
      },
      { 
        key: 'invitedAt', 
        label: 'Invited Date',
        format: formatDateForCSV
      },
      { 
        key: 'lastLogin', 
        label: 'Last Login',
        format: formatDateForCSV
      },
      { 
        key: 'createdAt', 
        label: 'Created Date',
        format: formatDateForCSV
      }
    ];

    const tenantSlug = userProfile?.tenant?.slug || 'tenant';
    const filename = `${tenantSlug}-users`;
    const result = exportToCSV(users, columns, filename);

    if (!result.success) {
      alert(`Failed to export CSV: ${result.error}`);
    }
  };

  // Hide Content Manager role selection unless the tenant_content flag is enabled
  const visibleRoles = availableRoles.filter(
    role => role.id !== 'ContentManager' || tenantContentEnabled
  );

  // Client-side filtering for instant feedback
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchQuery || 
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="flex flex-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">User Management</h2>
        <div className="flex gap-2">
          <button 
            onClick={handleDownloadCSV}
            className="btn btn-secondary"
            disabled={loading || users.length === 0}
            title="Download user list as CSV"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download CSV
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="btn btn-secondary"
            disabled={loading}
          >
            Import from CSV
          </button>
          <button
            onClick={regenerateAllPendingLinks}
            className="btn btn-secondary"
            disabled={loading}
            title="Create new invite links for all users who have not activated yet"
          >
            Regenerate all pending
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn btn-primary"
            disabled={loading}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Invite User
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filter-bar">
        <div className="filter-row">
          {/* Status Filter */}
          <div className="filter-group">
            <label className="form-label mb-2">Filter by Status</label>
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
              style={{ minWidth: '120px' }}
            >
              <option value="all">All Users</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          {/* Search */}
          <div className="search-group">
            <label className="form-label mb-2">Search</label>
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-input flex-1"
              />
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              {activeSearchQuery && (
                <button 
                  type="button"
                  onClick={handleClearSearch}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Clear
                </button>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* User Count */}
      {pagination.total > 0 && (
        <div className="mb-4 text-sm text-gray-600">
          Showing {filteredUsers.length} of {pagination.total} users
        </div>
      )}

      {/* Loading/Error States */}
      {loading ? (
        <div className="text-center py-8">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      ) : error ? (
        <div className="alert alert-error">
          <div>
            <strong>Error loading users:</strong> {error}
          </div>
          <button onClick={() => loadUsers()} className="btn btn-secondary btn-sm ml-3">
            Try Again
          </button>
        </div>
      ) : (
        /* User List */
        <div className="space-y-4">
          {filteredUsers.length === 0 ? (
            <div className="empty-state card">
              <div className="icon-container gray">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <h3>No users found</h3>
              <p>
                {statusFilter !== 'all' || searchQuery 
                  ? 'Try adjusting your filters or search terms.'
                  : 'Get started by inviting your first user.'
                }
              </p>
              {statusFilter === 'all' && !searchQuery && (
                <button 
                  onClick={() => setShowInviteModal(true)}
                  className="btn btn-primary"
                >
                  Invite First User
                </button>
              )}
            </div>
          ) : (
            filteredUsers.map(userData => (
              <UserCard
                key={userData.id}
                user={userData}
                currentUserId={userProfile?.id}
                tenantId={userProfile?.tenant?.id}
                availableRoles={visibleRoles}
                onUpdate={updateUser}
                onRegenerateLink={regenerateInviteLink}
                isLoading={loading}
                getStatusBadgeClass={getStatusBadgeClass}
                formatDate={formatDate}
              />
            ))
          )}
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onSubmit={inviteUser}
          availableRoles={visibleRoles}
          loading={loading}
        />
      )}

      {/* Freshly issued invite links */}
      {issuedLinks && (
        <InviteLinksModal
          title={issuedLinks.title}
          intro={issuedLinks.intro}
          links={issuedLinks.links}
          filename={`${userProfile?.tenant?.slug || 'tenant'}-invite-links`}
          onClose={() => setIssuedLinks(null)}
        />
      )}

      {/* Bulk CSV Import Modal */}
      {showImportModal && userProfile?.tenant?.id && (
        <BulkUserImportModal
          tenantId={userProfile.tenant.id}
          onClose={() => setShowImportModal(false)}
          onComplete={() => {
            setShowImportModal(false);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

// User Card Component - Updated with SuperAdmin protection and self-modification prevention
const UserCard = ({ user, currentUserId, tenantId, availableRoles, onUpdate, onRegenerateLink, isLoading, getStatusBadgeClass, formatDate }) => {
  const [showEditModal, setShowEditModal] = useState(false);

  // Check if user has SuperAdmin role
  const isSuperAdmin = user.roles && user.roles.includes('SuperAdmin');
  
  // Check if this is the current user viewing their own card
  const isCurrentUser = user.id === currentUserId;

  return (
    <>
      <div className="user-card">
        <div className="flex flex-between">
          <div className="flex-1">
            <div className="flex gap-3 items-center mb-2">
              <h3 className="text-lg font-semibold text-gray-800">
                {user.displayName || 'No Name'}
              </h3>
              <span className={getStatusBadgeClass(user.status)}>
                {user.status}
              </span>
              {isSuperAdmin && (
                <span className="status-pill" style={{ backgroundColor: '#f3e8ff', color: '#9333ea', fontSize: '0.7rem' }}>
                  Protected
                </span>
              )}
              {isCurrentUser && !isSuperAdmin && (
                <span className="status-pill" style={{ backgroundColor: '#dbeafe', color: '#1e40af', fontSize: '0.7rem' }}>
                  You
                </span>
              )}
            </div>
            
            <p className="text-sm text-gray-600 mb-2">{user.email}</p>
            
            <div className="flex gap-4 text-sm text-gray-500">
              <span>Roles: {user.roles?.join(', ') || 'None'}</span>
              {user.lastLogin && (
                <span>Last login: {formatDate(user.lastLogin)}</span>
              )}
              {user.invitedAt && (
                <span>Invited: {formatDate(user.invitedAt)}</span>
              )}
            </div>
          </div>
          
          <div className="user-actions">
            {/* Only show Edit button for non-SuperAdmin users and not for current user */}
            {!isSuperAdmin && !isCurrentUser && (
              <button
                onClick={() => setShowEditModal(true)}
                className="btn btn-secondary btn-sm"
                disabled={isLoading}
              >
                Edit
              </button>
            )}
            
            {/* Only show status change buttons for non-SuperAdmin users and not for current user */}
            {!isSuperAdmin && !isCurrentUser && (
              <>
                {user.status === 'suspended' ? (
                  <button
                    onClick={() => onUpdate(user.id, { status: 'active' })}
                    className="btn btn-sm"
                    style={{ backgroundColor: '#16a34a', color: 'white' }}
                    disabled={isLoading}
                  >
                    Reactivate
                  </button>
                ) : user.status === 'active' ? (
                  <button
                    onClick={() => onUpdate(user.id, { status: 'suspended' })}
                    className="btn btn-sm"
                    style={{ backgroundColor: '#dc2626', color: 'white' }}
                    disabled={isLoading}
                  >
                    Suspend
                  </button>
                ) : user.status === 'invited' ? (
                  <button
                    onClick={() => onRegenerateLink(user)}
                    className="btn btn-secondary btn-sm"
                    disabled={isLoading}
                    title="Create a new invite link to copy and share; the current link stops working"
                  >
                    Regenerate link
                  </button>
                ) : null}
              </>
            )}

            {/* Show info message for SuperAdmin users */}
            {isSuperAdmin && (
              <div className="text-xs text-gray-500 italic text-center">
                SuperAdmin accounts<br />cannot be modified
              </div>
            )}
            
            {/* Show info message for current user (non-SuperAdmin) */}
            {isCurrentUser && !isSuperAdmin && (
              <div className="text-xs text-gray-500 italic text-center">
                You cannot modify<br />your own account
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Only show edit modal for non-SuperAdmin users and not for current user */}
      {showEditModal && !isSuperAdmin && !isCurrentUser && (
        <EditUserModal
          user={user}
          currentUserId={currentUserId}
          tenantId={tenantId}
          availableRoles={availableRoles}
          onClose={() => setShowEditModal(false)}
          onSubmit={(updates) => {
            onUpdate(user.id, updates);
            setShowEditModal(false);
          }}
          loading={isLoading}
        />
      )}
    </>
  );
};

// Invite User Modal Component
const InviteUserModal = ({ onClose, onSubmit, availableRoles, loading }) => {
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    roles: ['User']
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRoleChange = (roleId, checked) => {
    setFormData(prev => ({
      ...prev,
      roles: checked 
        ? [...prev.roles, roleId]
        : prev.roles.filter(r => r !== roleId)
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (formData.roles.length === 0) {
      errors.roles = 'At least one role must be selected';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitError('');
    try {
      await onSubmit({
        email: formData.email.trim(),
        displayName: formData.displayName.trim() || undefined,
        roles: formData.roles
      });
    } catch (err) {
      console.error('Error submitting invite:', err);
      setSubmitError(err.message || 'Failed to send invitation. Please try again.');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Invite New User</h3>
          <button onClick={onClose} className="modal-close" disabled={loading}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="form-label" htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`form-input ${formErrors.email ? 'border-red-500' : ''}`}
              placeholder="user@company.com"
              disabled={loading}
              required
            />
            {formErrors.email && <p className="text-sm text-red-600 mt-1">{formErrors.email}</p>}
          </div>

          <div className="mb-4">
            <label className="form-label" htmlFor="displayName">Display Name (Optional)</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Jane Doe"
              disabled={loading}
            />
          </div>

          <div className="mb-6">
            <label className="form-label">Roles *</label>
            {formErrors.roles && <p className="text-sm text-red-600 mb-2">{formErrors.roles}</p>}
            <div className="checkbox-group">
              {availableRoles.map(role => (
                <div key={role.id || role.name} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.roles.includes(role.id)}
                    onChange={(e) => handleRoleChange(role.id, e.target.checked)}
                    disabled={loading}
                  />
                  <div className="checkbox-label">
                    <strong>{role.name}</strong>
                    {role.description && <div className="checkbox-description">{role.description}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {submitError && <p className="text-sm text-red-600 mb-4">{submitError}</p>}

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary flex-1" disabled={loading || !formData.email.trim()}>
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit User Modal Component - Updated with role change protection
const EditUserModal = ({ user, currentUserId, tenantId, availableRoles, onClose, onSubmit, loading }) => {
  const { user: authUser } = useAuth();
  const [formData, setFormData] = useState({
    roles: user.roles || [],
    status: user.status
  });
  const [validationError, setValidationError] = useState('');

  // Cohort membership is instantaneous - each toggle calls the cohort API
  // directly rather than being folded into formData/onSubmit, so it needs its
  // own loading state independent of the Status/Roles form below.
  const [cohorts, setCohorts] = useState([]);
  const [cohortsLoading, setCohortsLoading] = useState(false);
  const [memberCohortIds, setMemberCohortIds] = useState(
    () => new Set((user.cohorts || []).map(c => c.id))
  );
  const [pendingCohortId, setPendingCohortId] = useState(null);

  useEffect(() => {
    if (!tenantId) return;

    const loadCohorts = async () => {
      setCohortsLoading(true);
      try {
        const data = await apiService.get(`/api/v1/tenants/${tenantId}/cohorts`, authUser);
        if (data.success) {
          setCohorts(data.data.cohorts || []);
        }
      } catch (err) {
        console.error('Error loading cohorts:', err);
      } finally {
        setCohortsLoading(false);
      }
    };

    loadCohorts();
  }, [tenantId, authUser]);

  const toggleCohort = async (cohortId, checked) => {
    setPendingCohortId(cohortId);
    try {
      if (checked) {
        await apiService.post(`/api/v1/tenants/${tenantId}/cohorts/${cohortId}/members`, { userId: user.id }, authUser);
      } else {
        await apiService.delete(`/api/v1/tenants/${tenantId}/cohorts/${cohortId}/members/${user.id}`, authUser);
      }
      setMemberCohortIds(prev => {
        const next = new Set(prev);
        checked ? next.add(cohortId) : next.delete(cohortId);
        return next;
      });
    } catch (err) {
      console.error('Error updating cohort membership:', err);
    } finally {
      setPendingCohortId(null);
    }
  };

  // Check if this is the current user (should never happen due to UI protection, but safeguard)
  const isCurrentUser = user.id === currentUserId;

  // Define admin-level roles that should be protected
  const adminRoles = ['Admin', 'SuperAdmin', 'SuperContentManager'];
  
  // Check if user currently has any admin role
  const hasAdminRole = user.roles && user.roles.some(role => adminRoles.includes(role));
  
  // Check if the new role selection would remove all admin roles
  const wouldRemoveAdminAccess = () => {
    if (!hasAdminRole) return false; // User doesn't have admin access to begin with
    return !formData.roles.some(role => adminRoles.includes(role));
  };

  const handleRoleChange = (roleId, checked) => {
    const newRoles = checked 
      ? [...formData.roles, roleId]
      : formData.roles.filter(r => r !== roleId);
    
    setFormData(prev => ({
      ...prev,
      roles: newRoles
    }));
    
    // Clear validation error when user makes changes
    if (validationError) {
      setValidationError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate that at least one role is selected
    if (formData.roles.length === 0) {
      setValidationError('At least one role must be selected');
      return;
    }
    
    // Extra safeguard: prevent current user from removing their own admin access
    if (isCurrentUser && wouldRemoveAdminAccess()) {
      setValidationError('You cannot remove your own administrative access');
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Edit User: {user.displayName || user.email}</h3>
          <button onClick={onClose} className="modal-close" disabled={loading}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Show warning if this is somehow the current user */}
          {isCurrentUser && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> You are editing your own account. Removing your admin role will lock you out of admin functions.
              </p>
            </div>
          )}

          {/* Show validation error if present */}
          {validationError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{validationError}</p>
            </div>
          )}

          <div className="mb-4">
            <label className="form-label">Status</label>
            <select 
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="form-input"
              disabled={loading}
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              {user.status === 'invited' && <option value="invited">Invited</option>}
            </select>
          </div>

          <div className="mb-6">
            <label className="form-label">Roles</label>
            <div className="checkbox-group">
              {availableRoles.map(role => {
                const roleId = role.id;
                const isChecked = formData.roles.includes(roleId);
                const isAdminRole = adminRoles.includes(roleId);
                
                // Determine if this checkbox should show a warning indicator
                const isLastAdminRole = isCurrentUser && 
                                       hasAdminRole && 
                                       isAdminRole && 
                                       isChecked && 
                                       formData.roles.filter(r => adminRoles.includes(r)).length === 1;
                
                return (
                  <div key={role.id || role.name} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => handleRoleChange(roleId, e.target.checked)}
                      disabled={loading}
                    />
                    <div className="checkbox-label">
                      <strong>{role.name}</strong>
                      {isLastAdminRole && (
                        <span className="text-xs text-orange-600 ml-2">(Your last admin role)</span>
                      )}
                      {role.description && <div className="checkbox-description">{role.description}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-6">
            <label className="form-label">
              Cohorts
              <span className="text-xs text-gray-500 ml-2" style={{ fontWeight: 400 }}>
                (changes save immediately)
              </span>
            </label>
            {cohortsLoading ? (
              <p className="text-sm text-gray-500">Loading cohorts...</p>
            ) : cohorts.length === 0 ? (
              <p className="text-sm text-gray-500">No cohorts exist yet for this tenant.</p>
            ) : (
              <div className="checkbox-group">
                {cohorts.map(cohort => {
                  const isMember = memberCohortIds.has(cohort.id);
                  const isPending = pendingCohortId === cohort.id;

                  return (
                    <div key={cohort.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={isMember}
                        onChange={(e) => toggleCohort(cohort.id, e.target.checked)}
                        disabled={isPending}
                      />
                      <div className="checkbox-label">
                        <strong>{cohort.name}</strong>
                        {isPending && <span className="text-xs text-gray-500 ml-2">Saving...</span>}
                        {cohort.description && <div className="checkbox-description">{cohort.description}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary flex-1" disabled={loading}>
              {loading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// PropTypes
UserCard.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    email: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    status: PropTypes.string.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string),
    cohorts: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })),
    lastLogin: PropTypes.string,
    invitedAt: PropTypes.string
  }).isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tenantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  availableRoles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    description: PropTypes.string
  })).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRegenerateLink: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  getStatusBadgeClass: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired
};

InviteUserModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  availableRoles: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired
};

EditUserModal.propTypes = {
  user: PropTypes.object.isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tenantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  availableRoles: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
};

export default UserManagement;